import { ProductKeys3P } from '@atlassian/rovo-connectors-config/types';
import type { ProductKeys3P as ProductKeys3PType } from '@atlassian/rovo-connectors-config/types';

export const SMARTLINK_CONNECTION_LINK_COUNT_TRAIT =
	'confluence_3p_smartlink_connection_link_count';

export const SMARTLINK_CONNECTION_LINK_COUNT_BY_TEAM_TYPE_TRAIT_PREFIX =
	'confluence_3p_smartlink_connection_link_count_by_team_type_';

const SUFFICIENCY_THRESHOLD = 20;
export const DEFAULT_MAX_RECOMMENDED_APPS = 3;

// ---------------------------------------------------------------------------
// Shared HARDCODED_DEFAULTS + DEFAULT_FALLBACK_APPS table.
//
// Canonical source of truth for the cold-start / no-signal app list used by
// BOTH:
//   1. The Confluence onboarding modal (`use3pConnectorResources` →
//      `resolveRecommendedProductKeys`)
//   2. The post-office `confluence-3p-tools-nudge-banner`
//      (`implicit-message/defaults.ts` re-exports from here so the two
//      surfaces can never drift).
//
// Cascade tiers consumed by `resolveRecommendedProductKeys`:
//   Tier 1: per-team-type TAP trait (when sum of counts >= SUFFICIENCY_THRESHOLD)
//   Tier 2: site-wide TAP trait
//   Tier 3: HARDCODED_DEFAULTS[teamTypeKey] (falls through to
//           HARDCODED_DEFAULTS.na, which equals DEFAULT_FALLBACK_APPS, when
//           teamTypeKey is absent or unknown — so Tier 3 also acts as the
//           absolute "no signal at all" floor)
//
// Data provenance for the per-team-type rows: these defaults were derived by
// analysing third-party smartlink usage **by team type across all Confluence
// tenants** — i.e. the top apps each team type actually connects to in the
// wild. They are not arbitrary picks; they represent the most common
// real-world usage today and serve as a sensible "what would the average team
// in your role pick first?" starting point when we have no per-user /
// per-tenant signal to personalise with.
//
// `c_suite` is currently unreachable from the Confluence onboarding modal's
// `TEAM_TYPE_TO_KEY` map but is included for completeness so the table stays
// a faithful mirror of the upstream defaults dataset, and so adding a new
// `TeamType` later doesn't require touching this map.
//
// Order within each row is significant: apps are appended to the cascade
// result in this order, so the earlier an app appears, the more likely it
// survives the `MAX_APPS` cap during dedupe.
// ---------------------------------------------------------------------------

/**
 * The "no signal at all" fallback row. Single source of truth — used by:
 *   - `HARDCODED_DEFAULTS.na` below, which references this same constant so
 *     the per-team-type "na" row and the absolute floor can never drift.
 *     The cascade in `resolveRecommendedProductKeys` reaches this row via
 *     the `na` fallthrough in Tier 3.
 *   - The post-office banner's component-level prop default (so Storybook
 *     stories and any other prop-less render path still show something
 *     coherent).
 *   - The post-office implicit-message-creator's outer try/catch as the
 *     absolute "we crashed, here's something" floor.
 */
export const DEFAULT_FALLBACK_APPS: ReadonlyArray<string> = [
	'google-drive',
	'slack',
	'github',
];

/**
 * Hardcoded defaults per team-type key (snake_case to match the TAP trait
 * suffix convention).
 *
 * Keys are the same set of `team_type_key` strings used as TAP trait
 * suffixes — see `SMARTLINK_CONNECTION_LINK_COUNT_BY_TEAM_TYPE_TRAIT_PREFIX`.
 * The `na` row is the "no team type" bucket (both `TeamType === 'NA'` and
 * a fetch failure route here).
 */
/* eslint-disable camelcase -- Keys are upstream TAP trait keys, which use snake_case by contract. */
export const HARDCODED_DEFAULTS: Record<string, ReadonlyArray<string>> = {
	c_suite: ['google-drive', 'slack', 'github'],
	customer_service: ['google-drive', 'slack', 'onedrive'],
	data_science: ['google-drive', 'github', 'slack'],
	design: ['google-drive', 'figma', 'slack'],
	engineering: ['google-drive', 'github', 'slack'],
	finance: ['google-drive', 'slack', 'onedrive'],
	human_resources: ['google-drive', 'slack', 'onedrive'],
	information_technology: ['google-drive', 'slack', 'github'],
	legal: ['google-drive', 'slack', 'sharepoint'],
	marketing: ['google-drive', 'slack', 'figma'],
	// `na` is the "no signal" floor row — kept in sync with the standalone
	// `DEFAULT_FALLBACK_APPS` export above by referencing the same constant.
	na: DEFAULT_FALLBACK_APPS,
	operations: ['google-drive', 'slack', 'onedrive'],
	other: ['google-drive', 'github', 'slack'],
	product_management: ['google-drive', 'figma', 'slack'],
	program_management: ['google-drive', 'onedrive', 'sharepoint'],
	sales: ['google-drive', 'slack', 'sharepoint'],
};
/* eslint-enable camelcase */

/** Key used for the "no signal" cascade row — see `HARDCODED_DEFAULTS.na`. */
export const HARDCODED_DEFAULTS_NA_KEY = 'na';

export type SmartlinkConnectorRecommendationInputs = {
	siteWideSmartlinkAppCounts?: string;
	/**
	 * The `team_type_key` (snake_case) used as the lookup key into
	 * `HARDCODED_DEFAULTS` for Tier 3 of the cascade. When omitted, Tier 3
	 * falls through to the `na` row (which equals `DEFAULT_FALLBACK_APPS`),
	 * so callers that don't yet pass a team type keep their previous
	 * behaviour of only seeing the cold-start floor list.
	 */
	teamTypeKey?: string;
	teamTypeSmartlinkAppCounts?: string;
};

type ParsedAppCount = {
	appId: string;
	count: number;
};

const APP_ID_TO_PRODUCT_KEY_ALIASES: Record<string, ProductKeys3PType> = {
	drive: ProductKeys3P.GoogleDrive,
	figma: ProductKeys3P.Figma,
	github: ProductKeys3P.Github,
	'google-drive': ProductKeys3P.GoogleDrive,
	onedrive: ProductKeys3P.OneDrive,
	sharepoint: ProductKeys3P.Sharepoint,
	slack: ProductKeys3P.Slack,
};

export const parseAppCounts = (raw: string | undefined): ParsedAppCount[] => {
	if (!raw || raw.trim().length === 0) {
		return [];
	}

	return raw
		.split(',')
		.map((entry) => entry.trim())
		.filter((entry) => entry.length > 0)
		.map((entry) => {
			const separatorIndex = entry.indexOf(':');
			if (separatorIndex <= 0 || separatorIndex === entry.length - 1) {
				return undefined;
			}

			const appId = entry.slice(0, separatorIndex).trim();
			const count = Number(entry.slice(separatorIndex + 1).trim());

			if (appId.length === 0 || !Number.isFinite(count)) {
				return undefined;
			}

			return { appId, count };
		})
		.filter((entry): entry is ParsedAppCount => entry !== undefined);
};

export const topNonZeroAppsByCount = (parsed: ReadonlyArray<ParsedAppCount>): string[] =>
	parsed
		.map((entry, index) => ({ ...entry, index }))
		.filter((entry) => entry.count > 0)
		.sort((a, b) => {
			if (b.count !== a.count) {
				return b.count - a.count;
			}

			return a.index - b.index;
		})
		.map((entry) => entry.appId);

export const canonicalizeAppIdToProductKey = (appId: string): ProductKeys3PType | undefined => {
	return APP_ID_TO_PRODUCT_KEY_ALIASES[appId.toLowerCase()];
};

export const keepOnlySupportedProductKeys = (apps: ReadonlyArray<string>): ProductKeys3PType[] =>
	apps
		.map(canonicalizeAppIdToProductKey)
		.filter((productKey): productKey is ProductKeys3PType => productKey !== undefined);

export const dedupeMergeProductKeys = (
	existing: ReadonlyArray<ProductKeys3PType>,
	incoming: ReadonlyArray<ProductKeys3PType>,
	max: number,
): ProductKeys3PType[] => {
	const result: ProductKeys3PType[] = [];
	const seen = new Set<ProductKeys3PType>();

	for (const productKey of [...existing, ...incoming]) {
		if (!seen.has(productKey)) {
			seen.add(productKey);
			result.push(productKey);
			if (result.length >= max) {
				return result;
			}
		}
	}

	return result;
};

/**
 * Runs the 3-tier cascade and returns up to `maxRecommendedApps` canonical
 * product keys. Pure: takes already-fetched data, never throws.
 *
 *   Tier 1: per-team-type TAP trait, when `sum(counts) >= SUFFICIENCY_THRESHOLD`.
 *   Tier 2: site-wide TAP trait, backfilling Tier 1.
 *   Tier 3: HARDCODED_DEFAULTS[teamTypeKey] — team-type-curated defaults.
 *           Falls through to HARDCODED_DEFAULTS.na (which equals
 *           DEFAULT_FALLBACK_APPS) when teamTypeKey is absent or unknown,
 *           so this tier also acts as the absolute "no signal at all" floor.
 *           Skipped entirely when `options.traitOnly` is true — callers that
 *           use the result for *visual* fallbacks (e.g. picking which
 *           animation SVG to render) and have their own non-cascade fallback
 *           pattern should opt out so they only see trait-derived results.
 *
 * Apps are deduped after canonicalisation and the result is capped at
 * `maxRecommendedApps`. Unsupported app-ids (those without an entry in
 * `APP_ID_TO_PRODUCT_KEY_ALIASES`) are filtered at each tier so subsequent
 * tiers can still backfill empty slots — without this, an empty Tier-1
 * result that was empty because of filtering would prevent Tier-3 from
 * kicking in.
 */
export const resolveRecommendedProductKeys = (
	{
		siteWideSmartlinkAppCounts,
		teamTypeSmartlinkAppCounts,
		teamTypeKey,
	}: SmartlinkConnectorRecommendationInputs = {},
	maxRecommendedApps: number = DEFAULT_MAX_RECOMMENDED_APPS,
	options: { traitOnly?: boolean } = {},
): ProductKeys3PType[] => {
	let productKeys: ProductKeys3PType[] = [];

	// Tier 1 — per-team-type TAP trait, gated on sufficiency.
	//
	// The sufficiency check is intentionally performed against the raw
	// (pre-filter) sum: it reflects whether the site as a whole has enough
	// activity for this team type to trust the signal, independent of which
	// apps we happen to support today. Only positive counts contribute so a
	// malformed TAP row with negatives (`"github:50, x:-40"`) cannot drag the
	// total below the threshold.
	const teamTypeParsed = parseAppCounts(teamTypeSmartlinkAppCounts);
	const teamTypePositiveSum = teamTypeParsed.reduce(
		(sum, entry) => sum + Math.max(0, entry.count),
		0,
	);

	if (teamTypePositiveSum >= SUFFICIENCY_THRESHOLD) {
		productKeys = dedupeMergeProductKeys(
			[],
			keepOnlySupportedProductKeys(topNonZeroAppsByCount(teamTypeParsed)),
			maxRecommendedApps,
		);
	}

	// Tier 2 — site-wide TAP trait backfill.
	if (productKeys.length < maxRecommendedApps) {
		const siteWideParsed = parseAppCounts(siteWideSmartlinkAppCounts);
		productKeys = dedupeMergeProductKeys(
			productKeys,
			keepOnlySupportedProductKeys(topNonZeroAppsByCount(siteWideParsed)),
			maxRecommendedApps,
		);
	}

	// Tier 3 — HARDCODED_DEFAULTS for the user's team type.
	//
	// If the caller didn't pass a teamTypeKey, or passed one we don't have
	// a curated row for, fall through to the `na` row. Because
	// HARDCODED_DEFAULTS.na references DEFAULT_FALLBACK_APPS by identity,
	// this single tier covers both the team-type-known case and the
	// "no signal at all" floor — no separate Tier 4 is needed.
	//
	// Skipped when `options.traitOnly` is set so animation callers (which
	// have their own department-based fallback) can distinguish "real signal"
	// from "cold-start defaults".
	if (!options.traitOnly && productKeys.length < maxRecommendedApps) {
		const tier3AppIds =
			(teamTypeKey ? HARDCODED_DEFAULTS[teamTypeKey] : undefined) ??
			HARDCODED_DEFAULTS[HARDCODED_DEFAULTS_NA_KEY] ??
			[];
		productKeys = dedupeMergeProductKeys(
			productKeys,
			keepOnlySupportedProductKeys(tier3AppIds),
			maxRecommendedApps,
		);
	}

	return productKeys;
};
